import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { merge, of, Subject } from 'rxjs';
import { catchError, filter, switchMap, takeUntil } from 'rxjs/operators';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { InboxAutomatorModalService } from './inbox-automator-modal.service';
import { InboxAutomatorMailbox, InboxAutomatorService, InboxAutomatorState } from './inbox-automator.service';

/**
 * SD-1229: a mailbox's Inbox Automator state, as a small control beside it -- Active,
 * Activate, or Upgrade -- that opens the Inbox Automator modal. For mailboxes connected before
 * this shipped, or whose post-connect prompt was dismissed.
 */
@Component({
  selector: 'app-inbox-automator-pill',
  template: `
    <button type="button" class="iap" *ngIf="mode" [ngClass]="'iap--' + mode" (click)="open($event)"
            [attr.aria-label]="'Inbox Automator: ' + label">
      <span class="iap__dot" *ngIf="mode === 'active'" aria-hidden="true"></span>
      <svg *ngIf="mode === 'upgrade'" class="iap__icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <rect x="3.5" y="7" width="9" height="6.5" rx="1.5" stroke="currentColor" stroke-width="1.4" />
        <path d="M5.5 7V5a2.5 2.5 0 015 0v2" stroke="currentColor" stroke-width="1.4" />
      </svg>
      <svg *ngIf="mode === 'activate'" class="iap__icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path d="M11 2L4 11.5h5L8 18l7-9.5h-5L11 2z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" />
      </svg>
      <span class="iap__text">{{ showName ? 'Inbox Automator · ' : '' }}{{ label }}</span>
    </button>
  `,
  styleUrls: ['./inbox-automator-pill.component.scss']
})
export class InboxAutomatorPillComponent implements OnInit, OnChanges, OnDestroy {
  @Input() email: string;
  @Input() provider: string;
  /** Prefix the state with "Inbox Automator ·" where the context does not already say it. */
  @Input() showName = true;

  mode: 'active' | 'activate' | 'upgrade' | 'available' | null = null;
  private mailbox: InboxAutomatorMailbox | null = null;
  private reload$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  constructor(
    private inboxAutomator: InboxAutomatorService,
    private modal: InboxAutomatorModalService,
    private organizationService: OrganizationService
  ) { }

  get label(): string {
    switch (this.mode) {
      case 'active': return 'Active';
      case 'activate': return 'Activate';
      case 'upgrade': return 'Upgrade';
      case 'available': return 'Off';
      default: return '';
    }
  }

  ngOnInit(): void {
    merge(
      this.reload$,
      this.organizationService.selectedOrganizationId,
      this.inboxAutomator.changed$.pipe(filter(org => org === this.organizationService.selectedOrganization))
    ).pipe(
      takeUntil(this.destroy$),
      filter(() => !!this.email && !!this.organizationService.selectedOrganization),
      switchMap(() => this.inboxAutomator.state(this.organizationService.selectedOrganization)
        .pipe(catchError(() => of(null as InboxAutomatorState))))
    ).subscribe(state => this.apply(state));
  }

  ngOnChanges(): void {
    this.reload$.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private apply(state: InboxAutomatorState | null): void {
    this.mailbox = InboxAutomatorService.find(state, this.email);
    if (!state || !this.mailbox) { this.mode = null; return; }
    if (this.mailbox.isActive) { this.mode = 'active'; return; }
    if (state.entitled) { this.mode = 'activate'; return; }
    // Included in the plan but not the caller's to switch on: show the state, not a sale.
    this.mode = state.planIncludes ? 'available' : 'upgrade';
  }

  open(event: Event): void {
    // The pill can sit inside a clickable row; it acts on its own.
    event.stopPropagation();
    this.modal.open({ email: this.email, provider: this.provider || this.mailbox?.provider })
      .then(() => this.reload$.next());
  }
}
